import { FastForward, Loader2, Pause, Play, Rewind, SkipForward, Volume2, VolumeX } from 'lucide-react'
import { SEEK_STEP_SECONDS, formatAudioTime, useCurrentSpeechId, useSpeech } from '../../lib/speechPlayer'

/**
 * Persistent transport for whatever is being read aloud.
 *
 * The per-message Listen button carries its own inline transport, which is
 * fine until you scroll: the audio keeps playing from a bubble that is no
 * longer on screen, and pausing or seeking means hunting for the message it
 * came from. This bar is pinned to the composer instead, so the controls are
 * wherever the audio is — one fixed place, whatever the feed is doing.
 *
 * It follows the module-level player rather than any one message, so it also
 * covers voice-mode commentary and readings started from other panels.
 */
export function AudioTransportBar({
  summaries,
}: {
  /**
   * speech id → that turn's closing message on its own. Only turns whose
   * closing message differs from the whole-turn reading appear here; there is
   * nothing to skip ahead to otherwise.
   */
  summaries?: Map<string, string>
}) {
  const currentId = useCurrentSpeechId()
  // Driving the transport with the ACTIVE id (rather than a fixed one) is what
  // makes this follow the audio. The controls themselves are module-level, so
  // they act on the live playback regardless of which id opened it.
  const { speaking, paused, hasTransport, loading, progress, speak, stop, pause, resume, seekBy, seekTo } =
    useSpeech(currentId ?? '__cc_audio_bar_idle__')

  if (!currentId) return null
  const summaryText = summaries?.get(currentId) ?? null

  const control = 'inline-flex items-center justify-center h-7 w-7 rounded transition-colors hover:bg-secondary/60 text-muted-foreground hover:text-foreground shrink-0'

  return (
    <div className="mb-2 flex items-center gap-1 rounded-lg border border-primary/30 bg-primary/5 px-2 py-1.5 text-xs">
      {loading ? (
        <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin text-primary" aria-hidden="true" />
      ) : (
        <Volume2 className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
      )}

      {loading && !speaking ? (
        <span className="min-w-0 flex-1 truncate text-muted-foreground">Preparing audio…</span>
      ) : hasTransport ? (
        <>
          <button type="button" onClick={() => seekBy(-SEEK_STEP_SECONDS)} className={control} title="Back 10s" aria-label="Back 10 seconds">
            <Rewind className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={() => (paused ? resume() : pause())}
            className={control}
            title={paused ? 'Resume' : 'Pause'}
            aria-label={paused ? 'Resume reading' : 'Pause reading'}
          >
            {paused ? <Play className="h-3.5 w-3.5" /> : <Pause className="h-3.5 w-3.5" />}
          </button>
          <button type="button" onClick={() => seekBy(SEEK_STEP_SECONDS)} className={control} title="Forward 10s" aria-label="Forward 10 seconds">
            <FastForward className="h-3.5 w-3.5" />
          </button>
          {/* The track lengthens mid-play: later chunks are still being
              synthesized while the first ones play. */}
          <input
            type="range"
            min={0}
            max={Math.max(progress.duration, 0.1)}
            step={0.1}
            value={Math.min(progress.position, progress.duration)}
            onChange={(e) => seekTo(parseFloat(e.target.value))}
            aria-label="Audio position"
            className="h-1 min-w-0 flex-1 cursor-pointer accent-primary"
          />
          <span className="shrink-0 tabular-nums text-[10px] text-muted-foreground/70">
            {formatAudioTime(progress.position)}/{formatAudioTime(progress.duration)}
          </span>
        </>
      ) : (
        // The browser-voice fallback has no element behind it — nothing to
        // seek or pause, so offer only what actually works.
        <span className="min-w-0 flex-1 truncate text-muted-foreground">Reading aloud…</span>
      )}

      {/* Listen reads the WHOLE turn — every note since your prompt, which for
          a long turn is minutes of narration before the part that says what
          actually happened. This jumps straight to the closing message. */}
      {summaryText && (
        <button
          type="button"
          // Re-reads under the SAME id, so the bar stays put and the transport
          // simply restarts against the shorter text.
          onClick={() => speak(summaryText)}
          className="shrink-0 inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-[11px] font-medium text-muted-foreground transition-colors hover:bg-secondary/60 hover:text-foreground"
          title="Skip ahead to the closing summary"
        >
          <SkipForward className="h-3 w-3" />
          Summary
        </button>
      )}

      <button type="button" onClick={stop} className={control} title="Stop" aria-label="Stop reading">
        <VolumeX className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
